// Overview (static demo pages/Dashboard.jsx, screen 1 "Main" / mock-up 06)
// on real data: path bar, KPIs, stores on the Swiss map by worst state,
// "needs attention" from the active alarms, briefing, sales regions.
import { useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { opsApi, type AlarmRow } from "@gimasi/giothub-shell";
import Icon from "../components/Icon.jsx";
import SwissMap from "../components/SwissMap.jsx";
import { CountUp, Spark, Typewriter } from "../components/Viz.jsx";
import { PathBar } from "../components/Nav.jsx";
import { useT } from "../i18n";
import { current } from "../lib/account";
import { useLoad } from "../lib/load";
import { ALARM_ST, useFleet } from "../data/fleet";
import { inRange } from "../data/classes";
import { rollup, sites, type LocNode } from "../data/locations";
import { State } from "./common";
import { series as readSeries } from "../lib/series";

// label offsets of the demo (data/locations.js LABEL_POS in Dashboard.jsx)
export const LABEL_POS: Record<string, { lx?: number; ly?: number; anchor?: string }> = { basel: { ly: 32 }, zurich: { ly: -24 }, luzern: { lx: 22, ly: 20 }, lugano: { lx: 22, ly: 5, anchor: "start" }, bellinzona: { lx: 18, ly: -8, anchor: "start" }, chiasso: { lx: 13, ly: 4, anchor: "start" }, lausanne: { ly: -19 }, sion: { ly: 24 }, chur: { ly: 24 }, "st-gallen": { ly: 30 }, bern: { ly: 33 }, geneve: { ly: 34 }, winterthur: { ly: -22 } };
const DAY = 864e5;
const median = (xs: number[]) => { if (!xs.length) return null; const s = [...xs].sort((a, b) => a - b); return s[Math.floor(s.length / 2)]; };
const dayIdx = (iso: string, from: number) => Math.floor((Date.parse(iso) - from) / DAY);
const SEV = (type: string) => ALARM_ST[type] || "warn";
const ORDER: Record<string, number> = { crit: 0, alarm: 1, warn: 2, delayed: 3 };

/** 30-day history: alarms per day, time to acknowledge, time in range (hourly means). */
async function history(points: { id: string; regime: string }[]) {
  const to = Date.now(), from = to - 30 * DAY;
  const al = await opsApi.alarms({ from: new Date(from).toISOString(), limit: 500 });
  const raised = (al.items as AlarmRow[]).filter((a) => a.raised !== false);
  const perDay = Array.from({ length: 30 }, () => 0), ackDay: number[][] = Array.from({ length: 30 }, () => []);
  for (const a of raised) {
    const d = dayIdx(a.utc, from);
    if (d >= 0 && d < 30) perDay[d]++;
    if (a.ack_utc && d >= 0 && d < 30) ackDay[d].push((Date.parse(a.ack_utc) - Date.parse(a.utc)) / 60e3);
  }
  const ackAll = ackDay.flat();
  const inDay = Array.from({ length: 30 }, () => ({ n: 0, ok: 0 }));
  await Promise.all(points.filter((p) => p.regime).slice(0, 40).map(async (p) => {
    for (const key of ["ext_temperature", "temperature_1"]) {
      try {
        const s = await readSeries(p.id, { key, from: new Date(from).toISOString(), to: new Date(to).toISOString(), resolution: "1h" });
        if (!s.items.length) continue;
        for (const x of s.items) { const d = dayIdx(x.utc, from); if (d < 0 || d >= 30) continue; inDay[d].n++; if (inRange(p.regime, x.value)) inDay[d].ok++; }
        return;
      } catch { /* key not on this sensor */ }
    }
  }));
  const n = inDay.reduce((a, d) => a + d.n, 0), ok = inDay.reduce((a, d) => a + d.ok, 0);
  return {
    perDay, ackMedian: median(ackAll), ackSpark: ackDay.map((x) => median(x) ?? 0),
    inRange: n ? (ok / n) * 100 : null, inSpark: inDay.map((d) => (d.n ? (d.ok / d.n) * 100 : 100)),
  };
}

export default function Overview() {
  const t = useT();
  const f = useFleet(t.lang);
  const hist = useLoad(() => (f ? history(f.points) : Promise.resolve(null)), [f?.points.length]);
  const [briefStep, setBriefStep] = useState(0);
  if (!f) return <div class="page"><State loading /></div>;
  const root = f.root;
  const vis = sites(root);
  const r = { sites: vis.length, crit: vis.filter((s) => s.state?.st === "crit").length, alarm: vis.filter((s) => s.state?.st === "alarm").length };
  const cities = root.children.filter((c) => c.map).map((c) => { const rr = rollup(c); return { id: c.id, name: c.name, x: c.map!.x, y: c.map!.y, count: rr.sites, worst: rr.worst, ...(LABEL_POS[c.value] || {}) }; });
  const siteOf = new Map<string, LocNode>();
  vis.forEach((s) => s.points.forEach((p) => siteOf.set(p.id, s)));
  const incidents = [...f.alarms].sort((a, b) => ORDER[SEV(a.type)] - ORDER[SEV(b.type)] || Date.parse(b.utc) - Date.parse(a.utc));
  const delayed = f.points.filter((p) => p.st === "delayed").length;
  const recent = f.points.filter((p) => p.lastContact && Date.now() - Date.parse(p.lastContact) < 30 * 60e3).length;
  const top = incidents[0];
  const topP = top ? f.byId.get(top.device_id) : undefined;
  const brief: string[] = !f.points.length ? [t("x.brief.none")]
    : [
        top ? t("x.brief.top", { site: siteOf.get(top.device_id)?.name || "", cab: topP?.name || top.device_serial, state: t("st." + SEV(top.type)).toLowerCase(), v: t.temp(topP?.temp ?? null), time: t.time(new Date(top.utc)) })
            : t("x.brief.allOk", { n: f.points.length }),
        ...(delayed ? [t("x.brief.delayed", { n: delayed })] : []),
        t("x.brief.complete", { a: recent, b: f.points.length }),
      ];
  const regions = new Map<string, LocNode[]>();
  vis.forEach((s) => { const v = String(s.points[0]?.labels.sales_region || ""); if (v) regions.set(v, [...(regions.get(v) || []), s]); });
  const h = hist.data;
  const title = current.value?.kind === "customer" ? t("dash.titleCustomer", { name: current.value.name }) : t("dash.title");
  const now = new Date();
  return (
    <>
      {root.children.length > 0 && <PathBar node={root} scope={null} />}
      <div class="page">
        <div class="page-h">
          <div><h1>{title}</h1><div class="sub"><span class="live-dot" />{t.date(now)} · {t.time(now)} · {t("app.live")}</div></div>
          <div class="row"><button class="btn" type="button">{t("dash.last30")}</button><button class="btn pri" type="button">{t("dash.monthly")}</button></div>
        </div>
        <div class="kpis">
          <div class="card kpi"><span class="lbl">{t("kpi.storesAlarm")}</span><div class="v"><b style="color:var(--crit)"><CountUp value={r.crit + r.alarm} locale={t.locale} /></b><span>{t("kpi.of", { n: t.num(r.sites) })}</span></div><Spark data={h ? h.perDay : [0, 0]} color="var(--crit)" /><span class="foot">{t("kpi.critAlarm", { c: r.crit, a: r.alarm })}</span></div>
          <div class="card kpi"><span class="lbl">{t("kpi.inRange")}</span><div class="v"><b>{h?.inRange != null ? <CountUp value={h.inRange} decimals={1} locale={t.locale} /> : "—"}</b><span>% · 30 d</span></div><Spark data={h ? h.inSpark : [100, 100]} /><span class="foot">{t("x.kpi.inRangeFoot")}</span></div>
          <div class="card kpi"><span class="lbl">{t("kpi.ack")}</span><div class="v"><b>{h?.ackMedian != null ? <CountUp value={Math.round(h.ackMedian)} locale={t.locale} /> : "—"}</b><span>{t("kpi.minMedian")}</span></div><Spark data={h ? h.ackSpark : [0, 0]} /><span class="foot">{t("kpi.ackTarget")}</span></div>
          <div class="card kpi"><span class="lbl">{t("kpi.checks")}</span><div class="v"><b>—</b><span>{t("kpi.today")}</span></div><Spark data={[0, 0]} color="var(--warn)" /><span class="foot">{t("x.kpi.checksLater")}</span></div>
          <div class="card kpi"><span class="lbl">{t("kpi.loss")}</span><div class="v"><b>—</b><span>CHF</span></div><Spark data={[0, 0]} /><span class="foot">{t("x.kpi.lossLater")}</span></div>
        </div>
        <div class="grid" style="grid-template-columns:minmax(0,1.45fr) minmax(0,1fr)">
          <section class="card">
            <div class="card-h"><h2>{t("dash.byState")}</h2><div class="row" style="gap:14px;font-size:13.5px;color:var(--muted)">{[["crit", "#C8261B"], ["alarm", "#E3721C"], ["warn", "#C98A12"], ["ok", "#2E8B57"], ["delayed", "#8796A0"]].map(([s, c]) => <span key={s} class="row" style="gap:6px"><i style={{ width: "10px", height: "10px", borderRadius: "50%", background: c, display: "inline-block" }} />{t(s === "ok" ? "st.inRange" : "st." + s)}</span>)}</div></div>
            <SwissMap cities={cities} />
            <p style="margin:6px 0 0;color:var(--muted);font-size:13.5px">{t("dash.mapNote")}</p>
          </section>
          <section class="card">
            <div class="card-h"><h2>{t("dash.attention")}</h2><Link href="/alarms">{t("dash.allAlarms")}</Link></div>
            <div class="alist">
              {incidents.length === 0 && <p style="color:var(--muted);margin:0">{t("dash.allClear")}</p>}
              {incidents.slice(0, 8).map((a, idx) => {
                const p = f.byId.get(a.device_id); const sev = SEV(a.type);
                const mins = Math.max(0, Math.round((Date.now() - Date.parse(a.utc)) / 60e3));
                const what = sev === "delayed" ? t("x.at.NO_DATA") + " · " + (p?.name || a.device_serial) : `${p?.name || a.device_serial} · ${t.temp(p?.temp ?? null)}`;
                return (
                  <Link key={a.id} class={"aitem" + (idx === 0 ? " new" : "")} href={"/alarms/" + a.id}>
                    <span class={"st st-" + sev}>{t("st." + sev)}</span>
                    <span><b>{what}</b><small>{siteOf.get(a.device_id)?.name || ""}{" · "}{t("x.at." + a.type) === "x.at." + a.type ? a.type : t("x.at." + a.type)}</small></span>
                    <span class="mono" style="color:var(--muted);font-size:13.5px">{t.dur(mins)}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>
        <div class="grid" style={regions.size ? "grid-template-columns:minmax(0,1fr) minmax(0,1.2fr)" : "grid-template-columns:minmax(0,1fr)"}>
          <section class="card ai">
            <div class="card-h"><h2><Icon name="ai" size={20} />{t("x.briefing")}</h2><span class="when">{t("ai.updated", { t: t.time(new Date(f.at)) })}</span></div>
            <ul>{brief.slice(0, briefStep + 1).map((line, i) => { const [head, ...rest] = line.split("|"); return <li key={i}><b>{head}</b>{" "}{i === briefStep ? <Typewriter text={rest.join("|")} speed={12} onDone={() => setBriefStep((s) => Math.min(brief.length - 1, Math.max(s, i + 1)))} /> : rest.join("|")}</li>; })}</ul>
            <div class="row" style="margin-top:18px"><button class="btn pri" type="button">{t("ai.followup")}</button><button class="btn" type="button">{t("ai.ticket")}</button></div>
          </section>
          {regions.size > 0 && (
            <section class="card">
              <div class="card-h"><h2>{t("dash.regions")}</h2><span class="mono" style="color:var(--muted);font-size:12.5px">{t("dash.orgTree")}</span></div>
              <div style="overflow-x:auto"><table class="t">
                <thead><tr><th>{t("tbl.region")}</th><th class="n">{t("tbl.stores")}</th><th class="n">{t("x.sensors")}</th><th class="n">{t("x.openAlarms")}</th></tr></thead>
                <tbody>{[...regions].map(([name, ss]) => { const ids = new Set(ss.flatMap((s) => s.points.map((p) => p.id))); return <tr key={name}><td><b>{name}</b></td><td class="n">{ss.length}</td><td class="n">{ids.size}</td><td class="n">{f.alarms.filter((a) => ids.has(a.device_id)).length}</td></tr>; })}</tbody>
              </table></div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
